/**
 * Chat notification sound
 *
 * Plays a short two-note chime using the Web Audio API (no audio asset needed)
 * and stores the user's mute preference in localStorage.
 */

const STORAGE_KEY = 'chatNotificationSound';

let audioContext: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  if (audioContext) return audioContext;
  const Ctor =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  try {
    audioContext = new Ctor();
  } catch {
    return null;
  }
  return audioContext;
}

/** Whether chat notification sounds are enabled (defaults to true) */
export function isChatSoundEnabled(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== 'off';
  } catch {
    return true;
  }
}

export function setChatSoundEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, enabled ? 'on' : 'off');
  } catch {
    // Storage unavailable (private mode) - preference just won't persist
  }
}

/** Play the chat notification chime. Fails silently if audio is unavailable. */
export function playChatNotificationSound(): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }

  const start = ctx.currentTime;
  const notes = [
    { freq: 880, offset: 0 },     // A5
    { freq: 1318.5, offset: 0.1 } // E6
  ];

  for (const { freq, offset } of notes) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;

    const t = start + offset;
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(0.15, t + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.3);

    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(t);
    osc.stop(t + 0.32);
  }
}

/** Minimum gap between two chimes, so a burst of messages plays once */
const MIN_CHIME_INTERVAL_MS = 1500;
let lastChimeAt = 0;

export interface ChatChimeContext {
  /** The message is in a DM */
  isDm: boolean;
  /** The message @-mentions the user */
  mentionsYou: boolean;
  /** Its channel is the one on screen in the open drawer */
  onScreen: boolean;
  /** The user's own status */
  userStatus?: string;
}

/**
 * Chime for a new chat message from someone else, for now only a DM or an
 * @-mention. Silent when it is already seen (its channel is on screen and the
 * window has focus), in Do Not Disturb, when muted, or right after a chime.
 */
export function chimeForChatActivity({ isDm, mentionsYou, onScreen, userStatus }: ChatChimeContext): void {
  if (!isDm && !mentionsYou) return;
  if (userStatus === 'dnd' || !isChatSoundEnabled()) return;
  if (onScreen && !document.hidden && document.hasFocus()) return;

  const now = Date.now();
  if (now - lastChimeAt < MIN_CHIME_INTERVAL_MS) return;
  lastChimeAt = now;
  playChatNotificationSound();
}
